import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow, parseISO } from "date-fns";
import {
  Activity, Pencil, Plus, Trash2, ArrowRightCircle, Check, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";

const ACTION_ICON = {
  created: Plus,
  updated: Pencil,
  deleted: Trash2,
  status_changed: RefreshCw,
  converted: ArrowRightCircle,
  approved: Check,
};

const ACTION_COLOR = {
  created: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  updated: "bg-sky-100 text-sky-700 ring-sky-200",
  deleted: "bg-red-100 text-red-700 ring-red-200",
  status_changed: "bg-amber-100 text-amber-800 ring-amber-200",
  converted: "bg-indigo-100 text-indigo-700 ring-indigo-200",
  approved: "bg-emerald-100 text-emerald-700 ring-emerald-200",
};

function ActionLine({ entry }) {
  const Icon = ACTION_ICON[entry.action] || Activity;
  const cls = ACTION_COLOR[entry.action] || "bg-slate-100 text-slate-700 ring-slate-200";
  let extra = "";
  const meta = entry.meta || {};
  if (entry.action === "status_changed" && meta.to) {
    extra = `→ ${meta.to}`;
  } else if (entry.action === "updated" && Array.isArray(meta.fields)) {
    extra = meta.fields.length <= 4
      ? meta.fields.join(", ")
      : `${meta.fields.slice(0, 3).join(", ")} +${meta.fields.length - 3}`;
  } else if (entry.action === "created" && meta.total !== undefined) {
    extra = `${Number(meta.total).toLocaleString()} ${meta.currency || ""}`;
  } else if (entry.action === "converted" && meta.to_order) {
    extra = `→ ${meta.to_order}`;
  }
  return (
    <li className="flex items-start gap-3 py-2.5">
      <span
        className={`size-8 grid place-items-center rounded-full ring-1 ring-inset shrink-0 ${cls}`}
      >
        <Icon className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-foreground leading-tight">
          <span className="font-semibold">{entry.actor_name || "system"}</span>{" "}
          <span className="text-muted-foreground">
            {entry.action.replace("_", " ")}
          </span>{" "}
          {extra && (
            <span className="mono text-[11px] px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-700 ml-0.5">
              {extra}
            </span>
          )}
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5 mono">
          {entry.actor_role && (
            <span className="uppercase tracking-wider mr-2">
              {entry.actor_role}
            </span>
          )}
          {timeAgo(entry.ts)}
        </p>
      </div>
    </li>
  );
}

function timeAgo(iso) {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch (_) {
    return iso;
  }
}

/**
 * Renders a compact activity timeline for a single resource.
 *
 * Props:
 *   resourceType - "invoice" | "quotation" | "purchase_order" | "delivery_order" | "order"
 *   resourceId   - string
 *   compact      - when true, only shows a smaller card (no title header)
 */
export function ActivityLog({ resourceType, resourceId, compact }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["audit-logs", resourceType, resourceId],
    queryFn: () =>
      api.listAuditLogs({
        resource_type: resourceType,
        resource_id: resourceId,
        limit: 30,
      }),
    enabled: !!resourceId,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="activity-log-loading">
        Loading activity…
      </div>
    );
  }

  return (
    <div
      data-testid={`activity-log-${resourceType}`}
      className={`border border-border rounded-md bg-white ${compact ? "p-3" : "p-4"}`}
    >
      {!compact && (
        <div className="flex items-center gap-2 mb-2">
          <Activity className="size-3.5 text-primary" />
          <p className="overline">Activity</p>
          <span className="text-[10px] text-muted-foreground ml-auto">
            {data.length} event{data.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}
      {data.length === 0 ? (
        <p
          data-testid={`activity-log-empty-${resourceType}`}
          className="text-xs text-muted-foreground italic py-3 text-center"
        >
          No activity recorded yet — future edits and status changes will appear here.
        </p>
      ) : (
        <ul className="divide-y divide-border max-h-72 overflow-y-auto">
          {data.map((e) => (
            <ActionLine key={e.id} entry={e} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default ActivityLog;
